import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { Box, FormField, Select, SelectProps, SpaceBetween } from '@amzn/awsui-components-react';
import moment from 'moment';
import CONSTANTS from 'src/utils/constants';
import ServiceCollection from 'src/services/ServiceCollection';
import { PromotionRecord, PromotionStageInfo, getPromotionStageInfo } from 'src/models/promotion/PromotionTypes';
import { getFeatureFlags } from 'src/services/FeatureFlagsService';
import ErrorUtils from 'src/utils/ErrorUtils';
import StringUtils from 'src/utils/stringUtils';
import PromotionHistoryTable, { PromotionHistoryRow } from './PromotionHistoryTable';
import { PROMOTION_HISTORY_PERIOD_MONTHS, PROMOTION_HISTORY_DEFAULT_PREFERENCES, PROMOTION_HISTORY_PERIOD_URL_PARAM, PROMOTION_STATUS_INDICATOR_CONFIG } from './promotionHistoryConstants';
import DateUtils from 'src/utils/dateUtils';
import useLocalStorage from 'src/utils/useLocalStorage';

// 12 months anchored to the current calendar month, newest first. The option
// matching `openPeriod` (if any) gets a "(Open period)" suffix.
export function generatePeriodOptions(openPeriod?: string): SelectProps.Option[] {
    const start = moment().date(1);
    const openValue = (openPeriod || '').toUpperCase();
    return Array.from({ length: PROMOTION_HISTORY_PERIOD_MONTHS }, (_, i) => {
        const m = start.clone().subtract(i, 'months');
        const value = m.format('MMM-YY').toUpperCase();
        const label = m.format('MMM YYYY');
        return { value, label: value === openValue ? `${label} (Open period)` : label };
    });
}

export function toPromotionHistoryRows(records: PromotionRecord[]): PromotionHistoryRow[] {
    return records.map(record => ({
        ...record,
        promotedAtDisplay: record.promotedAt ? DateUtils.formatTimestamp(record.promotedAt) : '',
        componentNameDisplay: StringUtils.isNullOrEmpty(record.componentName) ? record.componentId : record.componentName as string,
        promotedByDisplay: (record.promotedBy || '').replace(/@amazon\.com$/, ''),
        statusDisplay: PROMOTION_STATUS_INDICATOR_CONFIG[record.status]?.[0] ?? record.status,
    }));
}

// URL-backed accounting-period param. Reads on every render; writes via
// history.replace so browser back/forward stays tied to URL state.
function usePeriodParam(): [string, (v: string) => void] {
    const history = useHistory();
    const location = useLocation();
    const value = new URLSearchParams(location.search).get(PROMOTION_HISTORY_PERIOD_URL_PARAM) || '';
    const setValue = useCallback((next: string) => {
        const params = new URLSearchParams(location.search);
        params.set(PROMOTION_HISTORY_PERIOD_URL_PARAM, next);
        history.replace({ pathname: location.pathname, search: `?${params.toString()}` });
    }, [location.search, location.pathname, history]);
    return [value, setValue];
}

// Outer component: flag gate + stage redirect. Keeps API-calling children out
// of the tree entirely for flag-off users and prod.
export default function PromotionHistoryView(props: { services: ServiceCollection }) {
    const stageInfo = getPromotionStageInfo(CONSTANTS.ENVIRONMENT_VARIABLES.TPE_UI_STAGE);
    const [featureEnabled, setFeatureEnabled] = useState<boolean | null>(null);
    useEffect(() => {
        getFeatureFlags().then(flags => setFeatureEnabled(flags.promotionEnabled));
    }, []);

    if (featureEnabled === false || !stageInfo) return <Redirect to={CONSTANTS.PAGE_NAV.DASHBOARD.URL} />;
    if (featureEnabled === null) return null;
    return <PromotionHistoryPage services={props.services} stageInfo={stageInfo} />;
}

function PromotionHistoryPage(props: { services: ServiceCollection; stageInfo: PromotionStageInfo }) {
    const { services, stageInfo } = props;

    // Table preferences live here (not in the table) because the page size drives
    // the server fetch limit.
    const [preferences, setPreferences] = useLocalStorage.getStorageKeyFromLocalStorage(
        'promotion-history-table-preferences',
        PROMOTION_HISTORY_DEFAULT_PREFERENCES,
    );
    const pageSize = preferences.pageSize || PROMOTION_HISTORY_DEFAULT_PREFERENCES.pageSize;

    // Backend open period → defaults + "(Open period)" label. Wait for the call
    // to settle before rendering options are usable, so we don't fetch the wrong
    // period first.
    const [handOffQuery] = useState((new Date()).toString());
    const [openPeriodRaw, , , handOffError] = services.mecService.getHandOffStatus(handOffQuery);
    const openPeriodValue = openPeriodRaw
        ? moment(openPeriodRaw, CONSTANTS.PERIOD_VALUE_FORMAT).format('MMM-YY').toUpperCase()
        : '';
    const isOpenPeriodSettled = !!openPeriodValue || !StringUtils.isNullOrEmpty(handOffError);
    const periodOptions = useMemo(() => generatePeriodOptions(openPeriodValue), [openPeriodValue]);

    // Selection derives from the URL — that's what makes back/forward work.
    // Once the hand-off call has settled, fall back to the open period, and
    // finally to the newest option (guards against an open period outside the
    // 12-month window leaving the table stuck loading).
    const [urlPeriod, setUrlPeriod] = usePeriodParam();
    const selectedPeriod: SelectProps.Option | null =
        periodOptions.find(o => o.value === urlPeriod)
        || (isOpenPeriodSettled
            ? periodOptions.find(o => o.value === openPeriodValue) || periodOptions[0] || null
            : null);

    // Boot-time URL sync: if the URL was missing/invalid, write the resolved value.
    useEffect(() => {
        if (selectedPeriod && selectedPeriod.value !== urlPeriod) {
            setUrlPeriod(selectedPeriod.value as string);
        }
    }, [selectedPeriod?.value, urlPeriod]);

    // Pagination + refresh
    const [currentPageIndex, setCurrentPageIndex] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const [refreshNonce, setRefreshNonce] = useState(0);
    const pageCacheRef = useRef<Record<number, { rows: PromotionHistoryRow[], nextToken: string | null }>>({});
    const [currentRows, setCurrentRows] = useState<PromotionHistoryRow[]>([]);
    const [loadedPageCount, setLoadedPageCount] = useState(0);
    const fetchIdRef = useRef(0);

    const fetchPage = useCallback(async (period: string, limit: number, token?: string) => {
        const result = await services.promotionService.getPromotionHistoryByPeriod(period, limit, token);
        return { rows: toPromotionHistoryRows(result.promotions || []), nextToken: result.nextToken || null };
    }, []);

    // Reset + fetch page 1 when the period changes or refresh is requested.
    useEffect(() => {
        if (!selectedPeriod?.value) return;
        fetchIdRef.current += 1;
        const id = fetchIdRef.current;
        pageCacheRef.current = {};
        setCurrentPageIndex(1);
        setLoadedPageCount(0);
        setIsLoading(true);
        fetchPage(selectedPeriod.value, pageSize)
            .then(result => {
                if (id !== fetchIdRef.current) return;
                pageCacheRef.current[1] = result;
                setCurrentRows(result.rows);
                setLoadedPageCount(1);
            })
            .catch(ex => {
                if (id !== fetchIdRef.current) return;
                setCurrentRows([]);
                services.messageService.showErrorBanner(ErrorUtils.getMessage(ex));
            })
            .finally(() => {
                if (id === fetchIdRef.current) setIsLoading(false);
            });
    }, [selectedPeriod?.value, pageSize, refreshNonce]);

    const goToPage = (pageIndex: number) => {
        const cached = pageCacheRef.current[pageIndex];
        if (cached) {
            setCurrentRows(cached.rows);
            setCurrentPageIndex(pageIndex);
        }
    };

    const fetchNextPage = async () => {
        const id = fetchIdRef.current;
        const lastPageToken = pageCacheRef.current[loadedPageCount]?.nextToken;
        if (!selectedPeriod?.value || !lastPageToken) return;
        setIsLoading(true);
        try {
            const result = await fetchPage(selectedPeriod.value, pageSize, lastPageToken);
            if (id !== fetchIdRef.current) return;
            const newPageIndex = loadedPageCount + 1;
            pageCacheRef.current[newPageIndex] = result;
            setCurrentRows(result.rows);
            setCurrentPageIndex(newPageIndex);
            setLoadedPageCount(newPageIndex);
        } catch (ex) {
            if (id !== fetchIdRef.current) return;
            services.messageService.showErrorBanner(ErrorUtils.getMessage(ex));
        } finally {
            if (id === fetchIdRef.current) setIsLoading(false);
        }
    };

    return (
        <div className="promotionHistoryView" data-class="promotionHistoryView">
            <SpaceBetween direction="vertical" size="m">
                <Box>
                    <SpaceBetween direction="horizontal" size="s" alignItems="center">
                        <FormField label="Accounting Period:" />
                        <div data-class="promotionHistoryPeriodSelect" style={{ minWidth: '260px' }}>
                            <Select
                                selectedOption={selectedPeriod}
                                onChange={({ detail }) => detail.selectedOption.value && setUrlPeriod(detail.selectedOption.value as string)}
                                options={periodOptions}
                                placeholder={isOpenPeriodSettled ? 'Select accounting period' : 'Loading periods...'}
                                disabled={!isOpenPeriodSettled}
                                statusType={isOpenPeriodSettled ? 'finished' : 'loading'}
                                loadingText="Loading periods..."
                            />
                        </div>
                    </SpaceBetween>
                </Box>
                <PromotionHistoryTable
                    rows={currentRows}
                    isLoading={isLoading}
                    stageInfo={stageInfo}
                    hasNextPage={!!pageCacheRef.current[loadedPageCount]?.nextToken}
                    currentPageIndex={currentPageIndex}
                    pagesCount={loadedPageCount}
                    onNextPage={fetchNextPage}
                    onPreviousPage={() => {}}
                    onPageChange={goToPage}
                    onRefresh={() => setRefreshNonce(n => n + 1)}
                    preferences={preferences}
                    onPreferencesChange={setPreferences}
                />
            </SpaceBetween>
        </div>
    );
}
